import { useState, useEffect, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectToken, selectUser } from '../redux/auth/selectors';

const baseUrl = 'wss://d2n59l2w1d.execute-api.us-east-2.amazonaws.com/dev/';

function useWebSocket(url = baseUrl) {
  const token = useSelector(selectToken);
  const user = useSelector(selectUser);
  const socketRef = useRef(null);

  const [isConnected, setConnection] = useState(false);

  const callbackListRef = useRef({});

  const connectWebSocket = useCallback(() => {
    const params = new URLSearchParams({ token });

    socketRef.current = new WebSocket(`${url}?${params}`);

    socketRef.current.onopen = () => {
      // console.log('Connection established!');
      setConnection(true);
      sendData({ id: user._id }, 'addOnlineUser');
    };

    socketRef.current.onclose = () => {
      // console.log('Connection closed!');
      setConnection(false);
    };

    socketRef.current.onmessage = event => {
      try {
        const response = JSON.parse(event.data);
        const { data, action } = response;
        const callback = callbackListRef.current[action];

        if (callback) {
          callback(data);
        }
      } catch (error) {
        // console.error('Error parsing message:', error);
      }
    };

    socketRef.current.onerror = event => {
      // console.log('WebSocket error:', event);
    };
  }, [token, url]);

  const sendData = useCallback(
    (data, action) => {
      // console.log(action, data);

      const body = { data, action, token };
      const hasRef = socketRef.current;
      const hasConnection = hasRef?.readyState === WebSocket.OPEN;
      if (!hasConnection) {
        connectWebSocket();
        console.error('WebSocket is not open.');
      }
      let countReconnect = 0;

      const intervalId = setInterval(() => {
        const hasRef = socketRef.current;
        const hasConnection = hasRef?.readyState === WebSocket.OPEN;
        if (hasConnection) {
          socketRef.current.send(JSON.stringify(body));
          clearInterval(intervalId);
        } else if (countReconnect > 5) {
          clearInterval(intervalId);
        } else {
          countReconnect++;
          // console.log('Attempt to reconnect');
        }
      }, 1000);
    },
    [token, connectWebSocket],
  );

  useEffect(() => {
    connectWebSocket();

    return () => {
      if (socketRef.current) socketRef.current.close();
    };
  }, [token, connectWebSocket]);

  useEffect(() => {
    const intervalId = setInterval(() => {
      if (isConnected) {
        sendData({}, 'ping');
      }
    }, 60000 * 5);
    return () => {
      clearInterval(intervalId);
    };
  }, [sendData, isConnected]);

  const addCallback = (action, callback) => {
    callbackListRef.current[action] = callback;
  };

  return {
    socket: socketRef.current,
    sendData,
    addCallback,
    isConnected,
  };
}

export default useWebSocket;
